<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <nav class="breadcrumb-nav mb-2">
                    <a href="/analytics" class="text-muted">Analytics</a>
                    <span class="mx-2 text-muted">/</span>
                    <a href="/analytics/programs" class="text-muted">Programs</a>
                    <span class="mx-2 text-muted">/</span>
                    <span class="text-gradient">{{ formatPubkey(address) }}</span>
                </nav>
                <h1 class="mb-2">
                    <span class="text-gradient">Program Analytics</span>
                </h1>
                <p class="font-mono text-muted mb-3">
                    <a :href="`https://solscan.io/account/${address}`" target="_blank" class="text-gradient">
                        {{ address }}
                    </a>
                </p>
                <date-range-picker default-range="24h" @change="handleDateRangeChange" />
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Transactions</div>
                <div class="hero-value">{{ formatCompact(summary.totalTransactions) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Total Fees</div>
                <div class="hero-value">{{ formatSOL(summary.totalFees) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Revenue</div>
                <div class="hero-value">{{ formatSOL(summary.totalRevenue) }}</div>
                <div class="hero-subtitle">fees + tips</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Unique Users</div>
                <div class="hero-value">{{ formatCompact(summary.totalUsers) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">CU Efficiency</div>
                <div class="hero-value">{{ summary.computeEfficiency }}%</div>
            </div>
            <div class="analytics-hero-stat" v-if="summary.totalSwaps > 0">
                <div class="hero-label">Swap Volume</div>
                <div class="hero-value">${{ formatCompact(summary.totalSwapVolume) }}</div>
            </div>
        </div>

        <!-- SECTION: Transaction Fee Composition -->
        <div class="section-divider">
            <span class="section-title">Transaction Fee Composition</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Priority Fees + Jito Tips</div>
                    <div class="panel-subtitle">Fees paid in transactions using this program</div>
                </div>
                <div class="panel-body">
                    <!-- Revenue breakdown bar -->
                    <div class="revenue-breakdown mb-4">
                        <div class="revenue-bar">
                            <div class="revenue-segment revenue-segment--base" :style="{ width: baseFeesPercent + '%' }">
                                <span v-if="baseFeesPercent > 8">Base</span>
                            </div>
                            <div class="revenue-segment revenue-segment--priority" :style="{ width: priorityFeesPercent + '%' }">
                                <span v-if="priorityFeesPercent > 8">Priority</span>
                            </div>
                            <div class="revenue-segment revenue-segment--tips" :style="{ width: tipsPercent + '%' }">
                                <span v-if="tipsPercent > 8">Tips</span>
                            </div>
                        </div>
                        <div class="revenue-total">
                            <span class="revenue-label">Total Revenue</span>
                            <span class="revenue-value">{{ formatSOL(summary.totalRevenue || 0) }}</span>
                        </div>
                    </div>

                    <!-- Fee metrics grid -->
                    <div class="fee-metrics-grid">
                        <div class="fee-metric-card">
                            <div class="fee-metric-header">Base Fees</div>
                            <div class="fee-metric-value" style="color: #6366f1;">{{ formatSOL(summary.baseFees || 0) }}</div>
                            <div class="fee-metric-percent">{{ baseFeesPercent }}% of revenue</div>
                        </div>
                        <div class="fee-metric-card">
                            <div class="fee-metric-header">Priority Fees</div>
                            <div class="fee-metric-value" style="color: #8b5cf6;">{{ formatSOL(summary.priorityFees || 0) }}</div>
                            <div class="fee-metric-percent">{{ priorityFeesPercent }}% of revenue</div>
                        </div>
                        <div class="fee-metric-card">
                            <div class="fee-metric-header">Jito Tips</div>
                            <div class="fee-metric-value" style="color: #f97316;">{{ formatSOL(summary.totalTips || 0) }}</div>
                            <div class="fee-metric-percent">{{ tipsPercent }}% of revenue</div>
                        </div>
                        <div class="fee-metric-card">
                            <div class="fee-metric-header">Fee per M CU</div>
                            <div class="fee-metric-value" style="color: #3b82f6;">{{ formatFeePerMCU(summary.avgFeePerCu) }}</div>
                            <div class="fee-metric-percent">lamports per million CU</div>
                        </div>
                        <div class="fee-metric-card">
                            <div class="fee-metric-header">Tip per M CU</div>
                            <div class="fee-metric-value" style="color: #10f7cf;">{{ formatFeePerMCU(summary.avgTipPerCu) }}</div>
                            <div class="fee-metric-percent">lamports per million CU</div>
                        </div>
                        <div class="fee-metric-card">
                            <div class="fee-metric-header">Avg Fee per Tx</div>
                            <div class="fee-metric-value" style="color: #ec4899;">{{ formatAvgFeePerTx }}</div>
                            <div class="fee-metric-percent">SOL per transaction</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Activity -->
        <div class="section-divider">
            <span class="section-title">Activity & Revenue</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Transaction Volume</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="txChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fees & Revenue</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="feesChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Fee Breakdown -->
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Composition Over Time</div>
                    <div class="panel-subtitle">Base | Priority | Jito Tips breakdown</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="feeBreakdownChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Performance -->
        <div class="section-divider">
            <span class="section-title">Performance & Usage</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Usage Over Time</div>
                    <div class="panel-subtitle">CU consumed by this program</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="computeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Compute Efficiency</div>
                    <div class="panel-subtitle">CU used vs CU requested (%)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="cuChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Unique Wallets per Day</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="usersChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: DeFi (if applicable) -->
        <div v-if="hasDefiActivity" class="section-divider">
            <span class="section-title">DeFi Activity</span>
        </div>
        <div v-if="hasDefiActivity" class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Swap Count</div>
                    <div class="panel-subtitle">Number of swaps over time</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="swapsChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Swap Volume</div>
                    <div class="panel-subtitle">USD volume over time</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="volumeChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>
        <div v-if="hasDefiActivity" class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">DeFi Metrics</div>
                </div>
                <div class="panel-body">
                    <div class="defi-metrics-grid">
                        <div class="defi-metric-card">
                            <div class="defi-metric-label">Total Swaps</div>
                            <div class="defi-metric-value" style="color: #10f7cf;">{{ formatCompact(summary.totalSwaps) }}</div>
                        </div>
                        <div class="defi-metric-card">
                            <div class="defi-metric-label">Total Volume</div>
                            <div class="defi-metric-value" style="color: #22c55e;">${{ formatCompact(summary.totalSwapVolume) }}</div>
                        </div>
                        <div class="defi-metric-card">
                            <div class="defi-metric-label">Avg Swap Size</div>
                            <div class="defi-metric-value" style="color: #8b5cf6;">${{ avgSwapSize }}</div>
                        </div>
                        <div class="defi-metric-card">
                            <div class="defi-metric-label">Avg Swaps/Tx</div>
                            <div class="defi-metric-value" style="color: #f59e0b;">{{ avgSwapsPerTx.toFixed(2) }}</div>
                        </div>
                    </div>
                    <!-- Inner vs Outer Swap Breakdown -->
                    <div class="swap-breakdown mt-4" v-if="summary.swapsInner > 0 || summary.swapsOuter > 0">
                        <div class="swap-breakdown-header">Swap Routing Breakdown</div>
                        <div class="swap-breakdown-grid">
                            <div class="swap-type-card">
                                <div class="swap-type-label">Direct Swaps</div>
                                <div class="swap-type-count">{{ formatCompact(summary.swapsOuter || 0) }}</div>
                                <div class="swap-type-volume">${{ formatCompact(summary.swapVolumeOuter || 0) }}</div>
                                <div class="swap-type-bar" :style="{ width: outerSwapPercent + '%', background: 'linear-gradient(90deg, #8b5cf6, #a855f7)' }"></div>
                            </div>
                            <div class="swap-type-card">
                                <div class="swap-type-label">Via Aggregator (CPI)</div>
                                <div class="swap-type-count">{{ formatCompact(summary.swapsInner || 0) }}</div>
                                <div class="swap-type-volume">${{ formatCompact(summary.swapVolumeInner || 0) }}</div>
                                <div class="swap-type-bar" :style="{ width: innerSwapPercent + '%', background: 'linear-gradient(90deg, #10f7cf, #22c55e)' }"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Detailed Metrics -->
        <div class="section-divider">
            <span class="section-title">Program Metrics</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Performance Summary</div>
                    <div class="panel-subtitle">Aggregated metrics for this program</div>
                </div>
                <div class="panel-body">
                    <div class="metrics-table-grid">
                        <div class="metrics-table-section">
                            <div class="metrics-table-header">Compute</div>
                            <table class="compass-table compact">
                                <tbody>
                                    <tr><td>Total CU</td><td class="text-end font-mono">{{ formatCompute(summary.totalCompute) }}</td></tr>
                                    <tr><td>Avg CU/Tx</td><td class="text-end font-mono">{{ formatCompact(summary.avgComputePerTx || 0) }}</td></tr>
                                    <tr><td>Median CU/Tx</td><td class="text-end font-mono">{{ formatCompact(summary.medianComputePerTx || 0) }}</td></tr>
                                    <tr><td>CU Efficiency</td><td class="text-end font-mono">{{ summary.computeEfficiency || 0 }}%</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="metrics-table-section">
                            <div class="metrics-table-header">Fees</div>
                            <table class="compass-table compact">
                                <tbody>
                                    <tr><td>Total Fees</td><td class="text-end font-mono">{{ formatSOL(summary.totalFees) }}</td></tr>
                                    <tr><td>Avg Fee/Tx</td><td class="text-end font-mono">{{ formatAvgFeePerTx }} SOL</td></tr>
                                    <tr><td>Fee per M CU</td><td class="text-end font-mono">{{ formatFeePerMCU(summary.avgFeePerCu) }}</td></tr>
                                    <tr><td>Fee per Write</td><td class="text-end font-mono">{{ formatMicro(summary.avgFeePerWrite) }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="metrics-table-section">
                            <div class="metrics-table-header">Jito Tips</div>
                            <table class="compass-table compact">
                                <tbody>
                                    <tr><td>Total Tips</td><td class="text-end font-mono">{{ formatSOL(summary.totalTips) }}</td></tr>
                                    <tr><td>Avg Tip/Tx</td><td class="text-end font-mono">{{ avgTipPerTx }} SOL</td></tr>
                                    <tr><td>Tip per M CU</td><td class="text-end font-mono">{{ formatFeePerMCU(summary.avgTipPerCu) }}</td></tr>
                                    <tr><td>Tips % of Rev</td><td class="text-end font-mono">{{ tipsPercent }}%</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="metrics-table-section">
                            <div class="metrics-table-header">Complexity</div>
                            <table class="compass-table compact">
                                <tbody>
                                    <tr><td>Avg CPI Depth</td><td class="text-end font-mono">{{ (summary.avgCpiDepth || 0).toFixed(2) }}</td></tr>
                                    <tr><td>Avg Accounts/Tx</td><td class="text-end font-mono">{{ (summary.avgAccountsPerTx || 0).toFixed(1) }}</td></tr>
                                    <tr><td>Avg Writes/Tx</td><td class="text-end font-mono">{{ (summary.avgWritesPerTx || 0).toFixed(1) }}</td></tr>
                                    <tr><td>Unique Users</td><td class="text-end font-mono">{{ formatCompact(summary.totalUsers) }}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Complexity Over Time</div>
                    <div class="panel-subtitle">CPI depth and accounts per transaction</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="complexityChart" :class="{ 'chart-canvas': true }"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Time Series Table -->
        <div class="section-divider">
            <span class="section-title">Detailed Time Series</span>
        </div>
        <div class="analytics-row cols-1">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Raw Data</div>
                    <div class="panel-controls">
                        <button class="compass-btn compass-btn--outline compass-btn--sm" @click="exportData">Export CSV</button>
                    </div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive">
                        <table class="compass-table time-series-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th class="text-end">Txns</th>
                                    <th class="text-end">Compute</th>
                                    <th class="text-end">CU Eff</th>
                                    <th class="text-end">Fees</th>
                                    <th class="text-end">Tips</th>
                                    <th class="text-end">Revenue</th>
                                    <th class="text-end">Users</th>
                                    <th class="text-end" v-if="hasDefiActivity">Swaps</th>
                                    <th class="text-end" v-if="hasDefiActivity">Volume</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in timeSeries.slice(-24)" :key="row.timestamp">
                                    <td>{{ row.label }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(row.txCount) }}</td>
                                    <td class="text-end font-mono">{{ formatCompute(row.compute) }}</td>
                                    <td class="text-end"><span :class="getEfficiencyClass(row.cuEfficiency / 100)">{{ row.cuEfficiency }}%</span></td>
                                    <td class="text-end font-mono">{{ row.fees.toFixed(4) }}</td>
                                    <td class="text-end font-mono" style="color: #f97316;">{{ row.tips.toFixed(4) }}</td>
                                    <td class="text-end font-mono" style="color: #22c55e;">{{ (row.fees + row.tips).toFixed(4) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(row.users) }}</td>
                                    <td class="text-end font-mono" v-if="hasDefiActivity">{{ formatCompact(row.numSwaps) }}</td>
                                    <td class="text-end font-mono" v-if="hasDefiActivity">${{ formatCompact(row.swapVolume || 0) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatCompute, formatSOL, formatPubkey, getEfficiencyClass, getEfficiencyColor } from '../composables/useFormatters';
import { colors, getLineChartOptions, getBarChartOptions, getStackedBarChartOptions } from '../composables/useChartConfig';

export default {
    name: 'ProgramDetail',
    props: { address: { type: String, required: true } },

    data() {
        return {
            currentRange: '24h',
            currentInterval: '1h',
            summary: {},
            timeSeries: [],
            refreshInterval: null,
            charts: {},
            colors: colors
        };
    },

    computed: {
        hasDefiActivity() { return this.summary.totalSwaps > 0; },
        avgSwapsPerTx() {
            if (!this.summary.totalTransactions) return 0;
            return this.summary.totalSwaps / this.summary.totalTransactions;
        },
        avgSwapSize() {
            if (!this.summary.totalSwaps || !this.summary.totalSwapVolume) return '0';
            const avg = this.summary.totalSwapVolume / this.summary.totalSwaps;
            return formatCompact(avg);
        },
        innerSwapPercent() {
            const inner = this.summary.swapsInner || 0;
            const outer = this.summary.swapsOuter || 0;
            const total = inner + outer;
            return total > 0 ? ((inner / total) * 100).toFixed(0) : 0;
        },
        outerSwapPercent() {
            const inner = this.summary.swapsInner || 0;
            const outer = this.summary.swapsOuter || 0;
            const total = inner + outer;
            return total > 0 ? ((outer / total) * 100).toFixed(0) : 0;
        },
        // Calculate percentages based on total revenue (base + priority + tips)
        baseFeesPercent() {
            const revenue = this.summary.totalRevenue || 0;
            const base = this.summary.baseFees || 0;
            if (revenue <= 0) return 0;
            return ((base / revenue) * 100).toFixed(1);
        },
        priorityFeesPercent() {
            const revenue = this.summary.totalRevenue || 0;
            const priority = this.summary.priorityFees || 0;
            if (revenue <= 0) return 0;
            return ((priority / revenue) * 100).toFixed(1);
        },
        tipsPercent() {
            const revenue = this.summary.totalRevenue || 0;
            const tips = this.summary.totalTips || 0;
            if (revenue <= 0) return 0;
            return ((tips / revenue) * 100).toFixed(1);
        },
        formatAvgFeePerTx() {
            const fees = this.summary.totalFees || 0;
            const txCount = this.summary.totalTransactions || 1;
            const avg = fees / txCount;
            if (avg >= 1) return avg.toFixed(2);
            if (avg >= 0.001) return avg.toFixed(4);
            return avg.toFixed(6);
        },
        avgTipPerTx() {
            const tips = this.summary.totalTips || 0;
            const txCount = this.summary.totalTransactions || 1;
            const avg = tips / txCount;
            if (avg >= 1) return avg.toFixed(2);
            if (avg >= 0.001) return avg.toFixed(4);
            return avg.toFixed(6);
        }
    },

    mounted() {
        this.initCharts();
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        // Use shared formatters
        formatCompact,
        formatCompute,
        formatSOL,
        formatPubkey,
        getEfficiencyClass,

        formatMicro(v) { return (v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); },

        // Format fee/tip per CU as per million CU for readability
        formatFeePerMCU(value) {
            if (!value) return '0';
            // Value is in lamports per CU, multiply by 1M for per-million-CU
            const perMillion = value * 1000000;
            if (perMillion >= 1e9) return (perMillion / 1e9).toFixed(2) + 'B';
            if (perMillion >= 1e6) return (perMillion / 1e6).toFixed(2) + 'M';
            if (perMillion >= 1e3) return (perMillion / 1e3).toFixed(1) + 'K';
            return perMillion.toFixed(0);
        },

        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.currentInterval = data.interval;
            this.loadData();
        },

        initCharts() {
            // Transaction Volume Chart
            this.charts.tx = markRaw(new Chart(this.$refs.txChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    formatCallback: v => formatCompact(v)
                })
            }));

            // Fees & Revenue Chart
            this.charts.fees = markRaw(new Chart(this.$refs.feesChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({
                    yTitle: 'SOL',
                    gradient: true
                })
            }));

            // Fee Breakdown Chart (stacked)
            this.charts.feeBreakdown = markRaw(new Chart(this.$refs.feeBreakdownChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({
                    formatCallback: v => v.toFixed(4) + ' SOL'
                })
            }));

            // Compute Usage Over Time Chart
            this.charts.compute = markRaw(new Chart(this.$refs.computeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({
                    formatCallback: v => formatCompute(v)
                })
            }));

            // CU Efficiency Chart
            this.charts.cu = markRaw(new Chart(this.$refs.cuChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({ yTitle: '%' }),
                    scales: {
                        y: {
                            min: 0,
                            max: 100,
                            title: { display: true, text: '%', color: '#9ca3af' },
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        x: {
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        }
                    }
                }
            }));

            // Users Chart
            this.charts.users = markRaw(new Chart(this.$refs.usersChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({
                    formatCallback: v => formatCompact(v),
                    gradient: true
                })
            }));

            // Swaps & Volume Charts - initialized lazily since canvases may not exist
            // (they're inside v-if="hasDefiActivity")
            this.charts.swaps = null;
            this.charts.volume = null;

            // Complexity Chart
            this.charts.complexity = markRaw(new Chart(this.$refs.complexityChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({})
            }));
        },

        async loadData() {
            try {
                const res = await fetch(`/analytics/api/program-detail/${this.address}?range=${this.currentRange}&interval=${this.currentInterval}`);
                const data = await res.json();
                this.summary = data.summary || {};
                this.timeSeries = data.timeSeries || [];
                this.updateCharts();
            } catch (e) { console.error(e); }
        },

        updateCharts() {
            const ts = this.timeSeries;
            if (!ts || ts.length === 0) return;
            const labels = ts.map(d => d.label);

            // Transaction Volume with gradient
            this.charts.tx.data = {
                labels,
                datasets: [{
                    label: 'Transactions',
                    data: ts.map(d => d.txCount),
                    backgroundColor: this.colors.primary,
                    borderWidth: 0
                }]
            };
            this.charts.tx.update();

            // Fees & Revenue with gradient fills
            this.charts.fees.data = {
                labels,
                datasets: [
                    {
                        label: 'Fees',
                        data: ts.map(d => d.fees),
                        borderColor: this.colors.primary,
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        fill: true,
                        borderWidth: 2,
                        tension: 0.4
                    },
                    {
                        label: 'Tips',
                        data: ts.map(d => d.tips),
                        borderColor: this.colors.accent,
                        backgroundColor: 'rgba(16, 247, 207, 0.1)',
                        fill: true,
                        borderWidth: 2,
                        tension: 0.4
                    }
                ]
            };
            this.charts.fees.update();

            // Fee Breakdown (stacked bar: Base | Priority | Tips)
            this.charts.feeBreakdown.data = {
                labels,
                datasets: [
                    {
                        label: 'Base Fees',
                        data: ts.map(d => d.baseFees || d.fees * 0.4), // Fallback if not available
                        backgroundColor: this.colors.feeBreakdown[0],
                        borderWidth: 0
                    },
                    {
                        label: 'Priority Fees',
                        data: ts.map(d => d.priorityFees || d.fees * 0.6),
                        backgroundColor: this.colors.feeBreakdown[1],
                        borderWidth: 0
                    },
                    {
                        label: 'Jito Tips',
                        data: ts.map(d => d.tips || 0),
                        backgroundColor: this.colors.feeBreakdown[2],
                        borderWidth: 0
                    }
                ]
            };
            this.charts.feeBreakdown.update();

            // Compute Usage Over Time
            this.charts.compute.data = {
                labels,
                datasets: [{
                    label: 'Compute Units',
                    data: ts.map(d => d.compute),
                    backgroundColor: this.colors.primary,
                    borderWidth: 0
                }]
            };
            this.charts.compute.update();

            // CU Efficiency with color gradient
            this.charts.cu.data = {
                labels,
                datasets: [{
                    label: 'CU Efficiency',
                    data: ts.map(d => d.cuEfficiency),
                    borderColor: '#f59e0b',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    fill: true,
                    borderWidth: 2,
                    tension: 0.4
                }]
            };
            this.charts.cu.update();

            // Users with gradient fill
            this.charts.users.data = {
                labels,
                datasets: [{
                    label: 'Users',
                    data: ts.map(d => d.users),
                    borderColor: '#ec4899',
                    backgroundColor: 'rgba(236, 72, 153, 0.1)',
                    fill: true,
                    borderWidth: 2,
                    tension: 0.4
                }]
            };
            this.charts.users.update();

            // Swaps (if DeFi program) - lazily initialize chart
            if (this.hasDefiActivity && this.$refs.swapsChart) {
                // Create chart if it doesn't exist yet
                if (!this.charts.swaps) {
                    this.charts.swaps = markRaw(new Chart(this.$refs.swapsChart, {
                        type: 'bar',
                        data: { labels: [], datasets: [] },
                        options: getBarChartOptions({
                            formatCallback: v => formatCompact(v)
                        })
                    }));
                }
                this.charts.swaps.data = {
                    labels,
                    datasets: [{
                        label: 'Swaps',
                        data: ts.map(d => d.numSwaps),
                        backgroundColor: this.colors.accent,
                        borderWidth: 0
                    }]
                };
                this.charts.swaps.update();
            }

            // Volume (if DeFi program) - lazily initialize chart
            if (this.hasDefiActivity && this.$refs.volumeChart) {
                // Create chart if it doesn't exist yet
                if (!this.charts.volume) {
                    this.charts.volume = markRaw(new Chart(this.$refs.volumeChart, {
                        type: 'bar',
                        data: { labels: [], datasets: [] },
                        options: getBarChartOptions({
                            formatCallback: v => '$' + formatCompact(v)
                        })
                    }));
                }
                this.charts.volume.data = {
                    labels,
                    datasets: [{
                        label: 'Volume (USD)',
                        data: ts.map(d => d.swapVolume || 0),
                        backgroundColor: '#22c55e',
                        borderWidth: 0
                    }]
                };
                this.charts.volume.update();
            }

            // Complexity
            this.charts.complexity.data = {
                labels,
                datasets: [
                    {
                        label: 'CPI Depth',
                        data: ts.map(d => d.cpiDepth),
                        borderColor: '#3b82f6',
                        borderWidth: 2,
                        tension: 0.4
                    },
                    {
                        label: 'Accounts/Tx',
                        data: ts.map(d => d.accountsP50),
                        borderColor: this.colors.primary,
                        borderWidth: 2,
                        tension: 0.4
                    }
                ]
            };
            this.charts.complexity.update();
        },

        exportData() {
            const headers = ['Time', 'Txns', 'Compute', 'CU Efficiency', 'Fees', 'Tips', 'Revenue', 'Users'];
            if (this.hasDefiActivity) {
                headers.push('Swaps', 'Volume USD');
            }
            const csv = [headers.join(',')];
            this.timeSeries.forEach(r => {
                const row = [
                    r.label,
                    r.txCount,
                    r.compute,
                    r.cuEfficiency,
                    r.fees,
                    r.tips,
                    r.fees + r.tips,
                    r.users
                ];
                if (this.hasDefiActivity) {
                    row.push(r.numSwaps, r.swapVolume || 0);
                }
                csv.push(row.join(','));
            });
            const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `program_${this.address.slice(0, 8)}_${this.currentRange}.csv`;
            a.click();
        }
    }
};
</script>

<style scoped>
.breadcrumb-nav { font-size: 0.85rem; }
.chart-tall { min-height: 280px; }
.hero-subtitle { font-size: 0.7rem; color: rgba(255,255,255,0.5); margin-top: 0.15rem; }
.hero-stat--cyan { border-top-color: #10f7cf; }
.font-mono { font-family: 'JetBrains Mono', monospace; }
.text-success { color: #22c55e; }
.text-warning { color: #f59e0b; }
.text-danger { color: #ef4444; }
.compass-btn--sm { padding: 0.3rem 0.6rem; font-size: 0.8rem; }

/* Metrics row styling (from NetworkOverview) */
.metrics-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.metric-item {
    text-align: center;
}

.metric-label {
    font-size: 0.8rem;
    color: #9ca3af;
    margin-bottom: 4px;
}

.metric-value {
    font-size: 1.25rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
}

.metric-subtitle {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 2px;
}

.text-gradient {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

/* Jito bar styling */
.jito-bar {
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: hidden;
}

.jito-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #8b5cf6, #f97316);
    border-radius: 4px;
    transition: width 0.3s ease;
}

.jito-bar-label {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    color: #9ca3af;
}

/* Revenue breakdown bar styling */
.revenue-breakdown {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.revenue-bar {
    display: flex;
    height: 32px;
    border-radius: 6px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.revenue-segment {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 500;
    color: white;
    transition: width 0.3s ease;
}

.revenue-segment--base {
    background: #6366f1;
}

.revenue-segment--priority {
    background: #8b5cf6;
}

.revenue-segment--tips {
    background: linear-gradient(90deg, #f97316, #fb923c);
}

.revenue-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.revenue-label {
    font-size: 0.85rem;
    color: #9ca3af;
}

.revenue-value {
    font-size: 1.25rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
    color: #22c55e;
}

/* Fee metrics grid */
.fee-metrics-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 12px;
}

@media (max-width: 1400px) {
    .fee-metrics-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .fee-metrics-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.fee-metric-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 12px;
    text-align: center;
}

.fee-metric-header {
    font-size: 0.75rem;
    color: #d1d5db;
    margin-bottom: 6px;
    font-weight: 500;
}

.fee-metric-value {
    font-size: 1.1rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
    margin-bottom: 4px;
}

.fee-metric-percent {
    font-size: 0.7rem;
    color: #9ca3af;
}

/* Efficiency grid styling */
.efficiency-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 16px;
}

@media (max-width: 1200px) {
    .efficiency-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .efficiency-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.efficiency-metric {
    text-align: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.06);
}

.efficiency-label {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-bottom: 4px;
}

.efficiency-value {
    font-size: 1.1rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
    color: #e5e7eb;
}

.efficiency-unit {
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 2px;
}

/* DeFi metrics grid */
.defi-metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
}

@media (max-width: 992px) {
    .defi-metrics-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.defi-metric-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 16px;
    text-align: center;
}

.defi-metric-label {
    font-size: 0.8rem;
    color: #d1d5db;
    margin-bottom: 8px;
    font-weight: 500;
}

.defi-metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
}

/* Swap breakdown section */
.swap-breakdown {
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.swap-breakdown-header {
    font-size: 0.85rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 12px;
}

.swap-breakdown-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.swap-type-card {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 8px;
    padding: 12px 16px;
    position: relative;
    overflow: hidden;
}

.swap-type-label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 4px;
}

.swap-type-count {
    font-size: 1.25rem;
    font-weight: 600;
    font-family: 'JetBrains Mono', monospace;
}

.swap-type-volume {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
    font-family: 'JetBrains Mono', monospace;
}

.swap-type-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    transition: width 0.3s ease;
}

/* Metrics table grid - 4 column layout */
.metrics-table-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
}

@media (max-width: 1200px) {
    .metrics-table-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .metrics-table-grid {
        grid-template-columns: 1fr;
    }
}

.metrics-table-section {
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    padding: 16px;
    border: 1px solid rgba(255, 255, 255, 0.06);
}

.metrics-table-header {
    font-size: 0.9rem;
    font-weight: 600;
    color: #e5e7eb;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.compass-table.compact {
    font-size: 0.85rem;
}

.compass-table.compact td {
    padding: 6px 0;
    border: none;
}

.compass-table.compact tr:last-child td {
    padding-bottom: 0;
}

/* Time series table - scrollable on small screens */
.time-series-table {
    font-size: 0.85rem;
}

.time-series-table th,
.time-series-table td {
    white-space: nowrap;
    padding: 8px 12px;
}

.time-series-table th {
    color: #d1d5db;
    font-weight: 500;
}
</style>
